import { Box, Button, Checkbox, CloseButton, Flex, HStack, Image, Input, InputGroup, InputLeftElement, InputRightElement, Text } from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { common, explore } from "../../constants/constants";
import { IoTriangleOutline } from "react-icons/io5";
import { FaUserAlt } from "react-icons/fa";
import { BiSearch } from "react-icons/bi";
const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const pplImg = `${IMAGE_BASE_PATH}/ui/Chevron/people.png`

// Matches the exact value each option's own checkbox already uses — kept as
// one helper so "Select All" selects precisely the same ids a manual
// click-through would. Only null/undefined count as "no value" here - a
// falsy-but-real value (false, 0, "") must survive as-is, or a boolean facet
// (e.g. Videos' isVisible) would have its "false" option silently coerced to
// an empty string and become impossible to select.
const optionValue = (it) => (it?._id === undefined || it?._id === null ? `` : it._id);

export default function FilterBody({
  filterOptions,
  data,
  Ukey,
  handleCheckboxChange,
  element,
  setApply,
}) {
  const [facetSearch, setFacetSearch] = useState("");

  // Narrows this facet's own already-loaded option list client-side (no
  // extra API call) — separate from FilterBox's top-level search, which
  // searches records themselves rather than a single facet's options.
  // Every facet (including Creator/Featured Artist) uses Algolia's own flat,
  // capped facet list here — values beyond Algolia's ~999-value display cap
  // are unreachable through this panel, which is an accepted limitation.
  const filteredData = useMemo(() => {
    const term = facetSearch.trim().toLowerCase();
    if (!term) return data;
    return (data || []).filter((it) => {
      const label = it?._id !== undefined && it?._id !== null ? String(it._id) : String(it ?? "");
      return label.toLowerCase().includes(term);
    });
  }, [data, facetSearch]);

  const selectedForFacet = filterOptions?.filters?.[element?.key] || [];
  const visibleIds = useMemo(
    () => (filteredData || []).map(optionValue).filter((id) => id !== "" && id !== undefined && id !== null),
    [filteredData],
  );
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedForFacet.includes(id));

  // Selects/deselects every currently-visible (search-filtered) option in one
  // go — a plain single-value toggle can't do this since each
  // handleCheckboxChange call only flips one id at a time (see
  // handleCheckboxChange in the owning page/hook: passing an array here
  // replaces the whole selection instead of toggling a single value).
  // Preserves any selection outside the current filtered view rather than
  // wiping the whole facet.
  const handleSelectAllToggle = () => {
    const next = allVisibleSelected
      ? selectedForFacet.filter((id) => !visibleIds.includes(id))
      : Array.from(new Set([...selectedForFacet, ...visibleIds]));
    handleCheckboxChange(Ukey, next);
  };

  return (
    <>
      {data && data?.length > 0 && (
        <Box px="1" pb="2">
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <BiSearch color="#999" />
            </InputLeftElement>
            <Input
              placeholder={explore.SEARCH}
              value={facetSearch}
              onChange={(e) => setFacetSearch(e.target.value)}
              fontSize="13px"
            />
            {facetSearch && (
              <InputRightElement>
                <CloseButton size="sm" onClick={() => setFacetSearch("")} />
              </InputRightElement>
            )}
          </InputGroup>
          {visibleIds.length > 0 && (
            <Button
              variant="link"
              size="xs"
              mt="1"
              colorScheme="blue"
              onClick={handleSelectAllToggle}
            >
              {allVisibleSelected ? explore.CLEAR_ALL : explore.SELECT_ALL}
            </Button>
          )}
        </Box>
      )}
      {filteredData && filteredData?.length > 0 ? (
        filteredData?.map((it, index) => {
          return (
            <>
              {(it?._id !== "" || it) && it?._id !== null && (
                <Flex
                  borderBottom={"1px"}
                  borderBottomColor={"color.lightgray5"}
                  key={index}
                >
                  <Checkbox
                    borderColor={"color.primary"}
                    _checked={{
                      "& .chakra-checkbox__control": {
                        background: "color.primary",
                      },
                    }}
                    iconColor="white"
                    cursor="pointer"
                    marginRight={"5px"}
                    isChecked={filterOptions?.filters?.[element?.key]?.includes(
                      it?._id
                    )}
                    onChange={() => {
                      handleCheckboxChange(Ukey, optionValue(it));
                      // setApply((prev) => !prev);
                    }}
                  />
                  <Box p="1">
                    {Ukey !== "type_of_event" ? (
                      <Text fontSize={"14px"}>
                        {typeof it?._id === "boolean"
                          ? it._id
                            ? common.YES
                            : common.NO
                          : (it?._id && it?._id !== "0" && it?._id !== "10000000") || it?._id === ""
                            ? it?._id
                            : it?._id === 0 || it?._id === "0" || it?._id === "10000000" || it?._id === 10000000
                              ? `${common.N_D} `
                              : it?._id}
                    {it?.count && ` (${it?.count})`}
                      </Text>
                    ) : (
                      <>
                        {it?._id === "Group Exhibition" ? (
                          <>
                            <Image
                              src={pplImg}
                              display="inline"
                              marginRight="8px"
                            />
                            {it?.count && `(${it?.count})`}
                          </>
                        ) : it?._id === "Triennale" ? (
                          <HStack>
                            <IoTriangleOutline
                              color="black"
                              display="inline"
                              fontSize="11px"
                            />
                            <Text>{it?.count && `(${it?.count})`}</Text>
                          </HStack>
                        ) : (
                          <HStack>
                            <FaUserAlt
                              color="black"
                              display="inline"
                              fontSize="11px"
                            />
                            <Text>{it?.count && `(${it?.count})`}</Text>
                          </HStack>
                        )}
                      </>
                    )}
                  </Box>
                </Flex>
              )}
            </>
          );
        })
      ) : data && data?.length > 0 ? (
        <Box p="1">
          <Text fontSize={"14px"}>{explore.NO_MATCHING_OPTIONS}</Text>
        </Box>
      ) : (
        <Box p="1">
          <Text fontSize={"14px"}>{common.CONTENT_AWAITED}</Text>
        </Box>
      )}
    </>
  );
}
